<template>
  <div>
    <div class="row g-3 mb-3">
      <div class="col-md-6">
        <div class="card p-3">
          <h6>Add Single Location</h6>
          <div class="row g-2">
            <div class="col-6"><MaterialInput v-model="singleLocation.city" label="City" /></div>
            <div class="col-6"><MaterialInput v-model="singleLocation.state_province" label="State / Province" /></div>
            <div class="col-6"><MaterialInput v-model="singleLocation.country" label="Country" /></div>
            <div class="col-6"><MaterialInput v-model="singleLocation.country_code" label="Country Code" /></div>
          </div>
          <div class="d-flex justify-content-end mt-2">
            <MaterialButton type="button" color="success" size="sm" :disabled="addingSingle" @click="addSingleLocation">
              Add Location
            </MaterialButton>
          </div>
        </div>
      </div>

      <div class="col-md-6">
        <div class="card p-3">
          <h6>Bulk Import</h6>
          <p class="text-sm text-secondary mb-1">One location per line: <code>City, State, Country, CountryCode</code></p>
          <textarea class="form-control" rows="5" v-model="bulkText" placeholder="Dallas, Texas, United States, US"></textarea>
          <div class="d-flex justify-content-end mt-2">
            <MaterialButton type="button" color="success" size="sm" :disabled="addingBulk" @click="addBulkLocations">
              Import
            </MaterialButton>
          </div>
        </div>
      </div>
    </div>

    <BaseTable
      :rows="rows"
      :columns="columns"
      idKey="id"
      :searchable="false"
      :selectable="false"
      :loading="loading"
      :pagination="pagination"
      @changePage="onPageChange"
      @updatePageSize="onPageSizeChange"
    >
      <template #buttons>
        <select class="form-select form-select-sm" style="width:auto;" v-model="statusFilter" @change="fetchLocations(1)">
          <option value="">All Statuses</option>
          <option value="pending">Pending</option>
          <option value="in_progress">In Progress</option>
          <option value="completed">Completed</option>
        </select>
      </template>

      <template #col-status="{ row }">
        <span class="badge bg-gradient-secondary" style="font-size:11px;">{{ row.status }}</span>
      </template>
      <template #actions="{ row }">
        <button class="btn btn-sm btn-outline-warning mb-0 py-1 px-2 me-1" title="Reset to pending" @click="resetLocation(row)">
          <i class="material-icons text-sm">restart_alt</i>
        </button>
        <button class="btn btn-sm btn-outline-danger mb-0 py-1 px-2" title="Delete" @click="deleteLocation(row)">
          <i class="material-icons text-sm">delete</i>
        </button>
      </template>
    </BaseTable>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import BaseTable from '@/components/Table/Table.vue'
import MaterialInput from '@/components/common/MaterialInput.vue'
import MaterialButton from '@/components/common/MaterialButton.vue'
import api from '@/config/api'

const loading = ref(true)
const rows = ref([])
const statusFilter = ref('')
const addingSingle = ref(false)
const addingBulk = ref(false)
const bulkText = ref('')

const singleLocation = reactive({
  city: '',
  state_province: '',
  country: '',
  country_code: '',
})

const pagination = ref({
  currentPage: 1,
  pageSize: 10,
  totalRecords: 0,
  totalPages: 0,
  hasPrevious: false,
  hasNext: false,
})

const columns = [
  { key: 'id', label: 'ID' },
  { key: 'city', label: 'CITY' },
  { key: 'state_province', label: 'STATE / PROVINCE' },
  { key: 'country', label: 'COUNTRY' },
  { key: 'country_code', label: 'CODE' },
  { key: 'status', label: 'STATUS' },
  { key: 'last_searched_at', label: 'LAST SEARCHED' },
]

async function fetchLocations(page = 1, pageSize = pagination.value.pageSize) {
  loading.value = true
  try {
    const response = await api.get('/admin/target-locations', {
      params: {
        page_number: page,
        page_size: pageSize,
        status: statusFilter.value,
      },
    })
    rows.value = response.data?.output || []
    const pag = response.data?.pagination || {}
    pagination.value = {
      currentPage: pag.current_page || 1,
      pageSize: pag.page_size || pageSize,
      totalRecords: pag.total_records || 0,
      totalPages: pag.total_pages || 0,
      hasPrevious: pag.has_previous || false,
      hasNext: pag.has_next || false,
    }
  } catch (e) {
    console.error('Error fetching target locations:', e)
    rows.value = []
  } finally {
    loading.value = false
  }
}

function onPageChange(page) { fetchLocations(page) }
function onPageSizeChange(size) { fetchLocations(1, size) }

async function addSingleLocation() {
  if (!singleLocation.city || !singleLocation.state_province || !singleLocation.country || !singleLocation.country_code) {
    return
  }
  addingSingle.value = true
  try {
    await api.post('/admin/target-locations', { ...singleLocation })
    singleLocation.city = ''
    singleLocation.state_province = ''
    singleLocation.country = ''
    singleLocation.country_code = ''
    await fetchLocations(1)
  } catch (e) {
    console.error('Error adding target location:', e)
  } finally {
    addingSingle.value = false
  }
}

function parseBulkText(text) {
  return text
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const [city, state_province, country, country_code] = line.split(',').map((part) => part.trim())
      return { city, state_province, country, country_code }
    })
    .filter((loc) => loc.city && loc.state_province && loc.country && loc.country_code)
}

async function addBulkLocations() {
  const locations = parseBulkText(bulkText.value)
  if (!locations.length) return

  addingBulk.value = true
  try {
    await api.post('/admin/target-locations', { locations })
    bulkText.value = ''
    await fetchLocations(1)
  } catch (e) {
    console.error('Error importing target locations:', e)
  } finally {
    addingBulk.value = false
  }
}

async function resetLocation(row) {
  try {
    await api.post(`/admin/target-locations/${row.id}/reset`)
    await fetchLocations(pagination.value.currentPage)
  } catch (e) {
    console.error('Error resetting target location:', e)
  }
}

async function deleteLocation(row) {
  try {
    await api.delete(`/admin/target-locations/${row.id}`)
    await fetchLocations(pagination.value.currentPage)
  } catch (e) {
    console.error('Error deleting target location:', e)
  }
}

onMounted(() => fetchLocations())
</script>
